import axios from "axios";
import { clearAuthSession, dispatchStorageSync } from './sessionStorage';

export type UserFacingErrorKind = 'auth' | 'network' | 'not_found' | 'validation' | 'server' | 'unknown';

export type UserFacingError = {
  kind: UserFacingErrorKind;
  message: string;
  retryable: boolean;
  statusCode: number | null;
};

const DEFAULT_TIMEOUT_MS = 15000;

const resolveBaseUrl = () => {
  const configuredBaseUrl = String(process.env.REACT_APP_API_URL || '').trim();
  const fallbackBaseUrl = 'http://localhost:4100/api/v1/';
  const baseUrl = configuredBaseUrl || fallbackBaseUrl;

  return baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
};

const normalizeServerMessage = (payload: any): string | null => {
  if (typeof payload?.message === 'string' && payload.message.trim()) return payload.message.trim();
  if (typeof payload?.error === 'string' && payload.error.trim()) return payload.error.trim();
  if (typeof payload === 'string' && payload.trim()) return payload.trim();
  return null;
};

export const getUserFacingError = (error: unknown, fallbackMessage = 'Terjadi kesalahan. Silakan coba lagi.'): UserFacingError => {
  if (!axios.isAxiosError(error)) {
    return {
      kind: 'unknown',
      message: fallbackMessage,
      retryable: true,
      statusCode: null,
    };
  }

  const statusCode = Number(error.response?.status) || null;
  const serverMessage = normalizeServerMessage(error.response?.data);

  if (error.code === 'ECONNABORTED') {
    return {
      kind: 'network',
      message: 'Permintaan memakan waktu terlalu lama. Coba lagi.',
      retryable: true,
      statusCode,
    };
  }

  if (!error.response) {
    return {
      kind: 'network',
      message: 'Koneksi bermasalah. Periksa internet Anda lalu coba lagi.',
      retryable: true,
      statusCode: null,
    };
  }

  if (statusCode === 401) {
    return {
      kind: 'auth',
      message: serverMessage || 'Sesi Anda berakhir. Silakan login kembali.',
      retryable: false,
      statusCode,
    };
  }

  if (statusCode === 404) {
    return {
      kind: 'not_found',
      message: serverMessage || 'Data yang Anda cari tidak ditemukan.',
      retryable: false,
      statusCode,
    };
  }

  if (statusCode === 400 || statusCode === 409 || statusCode === 422) {
    return {
      kind: 'validation',
      message: serverMessage || fallbackMessage,
      retryable: false,
      statusCode,
    };
  }

  if (statusCode >= 500) {
    return {
      kind: 'server',
      message: serverMessage || fallbackMessage,
      retryable: true,
      statusCode,
    };
  }

  return {
    kind: 'unknown',
    message: serverMessage || fallbackMessage,
    retryable: true,
    statusCode,
  };
};

const http = axios.create({
  baseURL: resolveBaseUrl(),
  timeout: DEFAULT_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
  },
});

http.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token && token !== '[object Object]' && !token.startsWith('{')) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

http.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      clearAuthSession();
      dispatchStorageSync();
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default http;
